/**
 * Account health for the SIGNED-IN ADMIN's own account.
 *
 * GET /api/me/v1/me/account-health — the same endpoint the portal reads, on the /me tree, resolving the
 * caller from their own session. It is not a platform surface and it is not about anybody else: an admin
 * cannot aim it at another user, because the user id comes from the bearer, never from a parameter.
 *
 * # Why this is reachable from the admin console at all
 *
 * It was not, until 2026-07-17. /me/account-health sat under RequireUser, which admits TypeFull only, and
 * an operator holds TypeAdmin — so the console had no way to tell its own user "you have one passkey and
 * one device loss between you and a support queue". RequireUserOrAdminSelfRead now admits that one read
 * and nothing else; an admin session is still refused at /me/credentials, PATCH /me, /me/apps and
 * /me/account-setup, and the gate matrix pins every one of those refusals.
 *
 * That gap mattered most for the cohort least able to absorb it. This product's escalation path bottoms
 * out at "contact your administrator", which is a dead end when you ARE the administrator. Migration
 * 000042 stopped the durability floor blocking at sign-in — it walled the founder out of a fresh account
 * holding a working passkey — on the bargain that a nudge replaces the wall. The portal had the nudge.
 * The console could not have one, because the data was unreachable.
 *
 * PARSE, don't cast: accountHealthResponseSchema is the same schema the portal uses, from auth-core. A
 * backend that renames a durability status must fail loudly here rather than mis-render a safety verdict.
 */

import { useQuery } from "@tanstack/react-query";
import {
  accountHealthResponseSchema,
  type AccountHealthResponse,
} from "@pureid/auth-core";

import { apiFetch } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

const ENDPOINT = "/api/me/v1/me/account-health";

export async function fetchAccountHealth(): Promise<AccountHealthResponse> {
  return accountHealthResponseSchema.parse(await apiFetch<unknown>(ENDPOINT));
}

export function useAccountHealthQuery() {
  return useQuery({
    queryKey: queryKeys.accountHealth,
    queryFn: fetchAccountHealth,
    // Durability changes only when the admin adds or removes a method, which they do from this very
    // page. No polling: an immutable-until-you-act read does not need a ticker, and a chip that
    // re-fetches on a timer is a chip that flickers for no reason. Invalidation on mutation is the
    // correct trigger and the only one.
    staleTime: 60_000,
  });
}
